// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [activeModules, setActiveModules] = useState([]);
    const [modulesLoaded, setModulesLoaded] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadPermissions = useCallback(async () => {
        try {
            const res = await api.get('/permissions/me');
            setPermissions(res.data.data || []);
        } catch {
            setPermissions([]);
        }
    }, []);

    const loadModules = useCallback(async () => {
        try {
            const res = await api.get('/system/modules');
            const keys = (res.data.data || []).filter((m) => m.is_active).map((m) => m.key);
            setActiveModules(keys);
        } catch {
            setActiveModules([]);
        } finally {
            setModulesLoaded(true);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('rems_token');
        const saved = localStorage.getItem('rems_user');
        if (token && saved) {
            try {
                setUser(JSON.parse(saved));
                api.get('/auth/me')
                    .then((res) => {
                        setUser(res.data.data);
                        localStorage.setItem('rems_user', JSON.stringify(res.data.data));
                        return Promise.all([loadPermissions(), loadModules()]);
                    })
                    .catch(() => {})
                    .finally(() => setLoading(false));
            } catch {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, [loadPermissions, loadModules]);

    const login = async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        const { user: u, token } = res.data.data;
        localStorage.setItem('rems_token', token);
        localStorage.setItem('rems_user', JSON.stringify(u));
        setUser(u);
        setModulesLoaded(false);
        await Promise.all([loadPermissions(), loadModules()]);
        return u;
    };

    const logout = () => {
        localStorage.removeItem('rems_token');
        localStorage.removeItem('rems_user');
        setUser(null);
        setPermissions([]);
        setActiveModules([]);
        setModulesLoaded(false);
    };

    const hasRole = (...roles) => {
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        return roles.includes(user.role);
    };

    const can = (key) => {
        if (!user) return false;
        if (user.role === 'super_admin' || user.role === 'admin') return true;
        return permissions.includes(key);
    };

    /**
     * Module visibility:
     * - Super admin always sees every module (so they can manage toggles).
     * - Everyone else only sees modules marked is_active by super admin.
     * - While modules are still loading, hide nothing (avoid flicker); after load, strict filter.
     */
    const moduleOn = (key) => {
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        if (!modulesLoaded) return true;
        return activeModules.includes(key);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                logout,
                hasRole,
                can,
                permissions,
                activeModules,
                modulesLoaded,
                moduleOn,
                reloadModules: loadModules,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
